/**
 * PhoneLinkBanner - the signed-in staff member has no phone linked.
 *
 * Every staff member links their own phone (decision D, 2026-10-10): it is
 * how they sign in with a code, and approving money needs a session opened
 * that way. Shown until the phone is linked on /account.
 */
import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { hasLinkedPhone } from '@lipaflow/shared'
import { DevicePhoneMobileIcon, XMarkIcon } from '@heroicons/react/24/outline'

export const LINKED_PHONE_QUERY_KEY = ['session', 'linked-phone'] as const

export default function PhoneLinkBanner() {
  const { pathname } = useLocation()
  const [dismissed, setDismissed] = useState(false)
  const linked = useQuery({
    queryKey: LINKED_PHONE_QUERY_KEY,
    queryFn: hasLinkedPhone,
    staleTime: 5 * 60_000,
  })

  // null: no Firebase session, so there is nothing to link here.
  if (linked.data !== false || dismissed || pathname === '/account') return null

  return (
    <div
      role="status"
      className="bg-accent-soft border-b border-hairline text-ink px-4 py-2 flex items-start justify-between gap-3"
    >
      <div className="flex items-start gap-2 font-inter text-body-sm">
        <DevicePhoneMobileIcon className="h-5 w-5 flex-shrink-0 text-accent" />
        <p>
          Link your phone so you can sign in with a code. Approving payments, refunds and payouts
          needs it.{' '}
          <Link to="/account" className="font-semibold underline">
            Link your phone
          </Link>
        </p>
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="p-1 rounded hover:bg-surface-alt transition-colors"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  )
}
